import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

@Component({
  selector: 'ef-fullcalendar',
  template: `<div #calendar></div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FullCalendarComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('calendar', { static: true }) calendarEl!: ElementRef;
  
  @Input() events: any[] = [];
  @Input() options: any = {};
  
  @Output() dateClick = new EventEmitter<any>();
  @Output() eventClick = new EventEmitter<any>();
  @Output() select = new EventEmitter<any>();
  @Output() datesRender = new EventEmitter<any>();
  @Output() eventRender = new EventEmitter<any>();

  private calendar: Calendar | null = null;

  ngOnInit() {
    // Component initialization
  }

  ngAfterViewInit() {
    this.initializeCalendar();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.calendar) {
      if (changes['events'] && changes['events'].currentValue) {
        this.updateEvents();
      }
      if (changes['options'] && changes['options'].currentValue) {
        this.updateOptions();
      }
    }
  }

  ngOnDestroy() {
    if (this.calendar) {
      this.calendar.destroy();
    }
  }

  private initializeCalendar() {
    const defaultOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      initialView: 'dayGridMonth',
      editable: true,
      selectable: true,
      selectMirror: true,
      dayMaxEvents: true,
      weekends: true,
      events: this.events || [],
      
      // Event handlers
      dateClick: (info: any) => {
        this.dateClick.emit(info);
      },
      eventClick: (info: any) => {
        this.eventClick.emit(info);
      },
      select: (info: any) => {
        this.select.emit(info);
      },
      datesSet: (info: any) => {
        this.datesRender.emit(info);
      },
      eventDidMount: (info: any) => {
        this.eventRender.emit(info);
      }
    };

    // Merge custom options with defaults
    const calendarOptions = { ...defaultOptions, ...this.options };

    this.calendar = new Calendar(this.calendarEl.nativeElement, calendarOptions);
    this.calendar.render();
  }

  private updateEvents() {
    if (this.calendar && this.events) {
      this.calendar.removeAllEvents();
      this.calendar.addEventSource(this.events);
    }
  }

  private updateOptions() {
    if (this.calendar) {
      // For option updates, we might need to recreate the calendar
      this.calendar.destroy();
      this.initializeCalendar();
    }
  }

  // Public methods to expose FullCalendar API
  public getCalendar(): Calendar | null {
    return this.calendar;
  }

  public next() {
    if (this.calendar) {
      this.calendar.next();
    }
  }

  public prev() {
    if (this.calendar) {
      this.calendar.prev();
    }
  }

  public today() {
    if (this.calendar) {
      this.calendar.today();
    }
  }

  public gotoDate(date: any) {
    if (this.calendar) {
      this.calendar.gotoDate(date);
    }
  }

  public getView() {
    return this.calendar ? this.calendar.view : null;
  }
}
